import { BoldLabelText } from '@/components/BoldLabelText/BoldLabelText';
import {
  DescriptionCaption,
  DescriptionCol,
  DescriptionRow,
} from '@/components/Description';
import { ComponentTypeMap } from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemGeneralForm';
import {
  ComponentType,
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemFormType,
} from '@/features/transaction_items/List/type';
import { useTranslation } from '@/i18n';
import { FormSelect, Label } from '@moneyforward/ap-frontend-components';
import { FC, memo, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import styles from './ComponentTypeSelect.module.scss';

export type Props = {
  method: UseFormReturn<TransactionItemFormType>;
  onChangeAfter: (
    componentType: TransactionItemFormType['component_type']
  ) => void;
  mode: TransactionItemDrawerMode;
};

export const ComponentTypeSelect: FC<Props> = memo(
  ({ method, onChangeAfter, mode }) => {
    const { t } = useTranslation();
    const { getValues, control } = method;

    const selectProps = useMemo(() => {
      return {
        className: styles['select-field'],
        options: [
          ...Object.keys(ComponentTypeMap).map((key) => ({
            label: t(`transaction_items.drawer.component_type.${key}`),
            value: key,
          })),
        ],
      };
    }, [t]);

    const rules = useMemo(() => {
      return {
        required: {
          value: true,
          message: t('transaction_items.validation_message.required'),
        },
      };
    }, [t]);

    return (
      <DescriptionRow>
        <DescriptionCol
          label={
            <Label
              required={mode !== TransactionItemDrawerModeEnum.DETAIL}
              testId='component_type'
            >
              <BoldLabelText>
                {t('transaction_items.table.component_type')}
              </BoldLabelText>
            </Label>
          }
          labelWidth='176px'
        />
        <DescriptionCol
          value={
            mode === TransactionItemDrawerModeEnum.CREATE ? (
              <div className={styles['type-select-wrapper']}>
                <FormSelect<TransactionItemFormType>
                  name='component_type'
                  control={control}
                  inputId='component_type'
                  testId='component_type'
                  selectProps={selectProps}
                  rules={rules}
                  onChangeAfter={(value) => {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    onChangeAfter(value as ComponentType);
                  }}
                />
                <DescriptionCaption>
                  {t('transaction_items.component_type.create.caption')}
                </DescriptionCaption>
              </div>
            ) : (
              <div>
                <div data-testid='component_type-value'>
                  {t(
                    `transaction_items.drawer.component_type.${getValues(
                      'component_type'
                    )}`
                  )}
                </div>
                {mode === TransactionItemDrawerModeEnum.EDIT && (
                  <DescriptionCaption>
                    {t('transaction_items.component_type.edit.caption')}
                  </DescriptionCaption>
                )}
              </div>
            )
          }
        />
      </DescriptionRow>
    );
  }
);
ComponentTypeSelect.displayName = 'ComponentTypeSelect';
